import { AppShell, Box } from '@mantine/core';
import { useDisclosure, useHotkeys, useLocalStorage } from '@mantine/hooks';
import { AppHeightAtom } from 'components/FixAppHeight';
import { useAtomValue } from 'jotai';
import { UblImportProgressNotifier } from 'modules/Import/UblImportProgressNotifier';
import { NavBar } from 'modules/Navigation';
import { GroupNavBar } from 'modules/Navigation/GroupNavBar';
import { doesLocationBelongToPath } from 'modules/Navigation/utils';
import { useHistory, useLocation } from 'react-router';
import { useTrackPreviousPathname } from 'shared/atoms/previous-pathname';
import { GROUP_ROUTE } from 'shared/constants';
import { useCustomVariablesSubscription } from 'shared/hooks/use-custom-variables-subscription';
import { useIsMobile } from 'shared/hooks/utils/use-is-mobile';
import { useTenant } from '../TenantValidator';
import { WarningBanners } from './WarningBanners';

interface PrivateRoutesWrappedProps extends React.PropsWithChildren {}

export const PrivateRoutesWrapper = ({ children }: PrivateRoutesWrappedProps) => {
  const tenant = useTenant();
  const history = useHistory();
  const { pathname } = useLocation();
  const isInGroup = doesLocationBelongToPath({ locationPath: pathname, path: GROUP_ROUTE });

  useTrackPreviousPathname();
  useCustomVariablesSubscription();
  const isMobile = useIsMobile();
  const appHeight = useAtomValue(AppHeightAtom);
  const isAppHeightFixed = appHeight.mode === 'hidden';
  const [isNavbaropenOnMobile, { close: closeMobile, open: openMobile }] = useDisclosure();
  const [isNavbarOpenOnDesktop, setIsNavbarOpenOnDesktop] = useLocalStorage({
    key: 'open-navbar',
    defaultValue: true,
  });

  const toggleDesktop = () => setIsNavbarOpenOnDesktop(state => !state);
  const navbarProps = {
    onClickOpenNavbar: openMobile,
    closeNavbarOnMobile: isMobile ? closeMobile : () => {},
  };

  useHotkeys([
    ['mod+B', toggleDesktop],
    ['mod+H', () => history.push('/dashboard')],
  ]);

  return (
    <AppShell
      padding="xl"
      navbar={{
        width: 258,
        breakpoint: 'mobile',
        collapsed: { mobile: !isNavbaropenOnMobile, desktop: !isNavbarOpenOnDesktop },
      }}
      styles={{
        navbar: { zIndex: 10 },
        main: { zIndex: 0 },
      }}
    >
      {tenant.onboarding.isComplete && (
        <AppShell.Navbar>
          {isInGroup ? <GroupNavBar {...navbarProps} /> : <NavBar {...navbarProps} />}
        </AppShell.Navbar>
      )}
      <AppShell.Main
        style={{
          overflowY: appHeight.mode,
          height: '100vh',
          paddingTop: '20px',
          ...(isAppHeightFixed && { display: 'flex', flexDirection: 'column' }),
        }}
        pb={appHeight.paddingBottom}
        bg="gray.0"
      >
        <WarningBanners />
        <UblImportProgressNotifier />
        {/* Always rendered so toggling FixAppHeight never remounts the page. When the height is
            fixed, the page takes what the banners leave instead of a full 100vh. */}
        <Box style={isAppHeightFixed ? { flex: 1, minHeight: 0 } : { display: 'contents' }}>
          {children}
        </Box>
      </AppShell.Main>
    </AppShell>
  );
};
